import React, { useRef, useState } from 'react';
import { Wand2, AudioWaveform, Mic2, Ear, LogOut, HelpCircle } from 'lucide-react';
import { signOutUser } from '../config/firebase';
import QuotaDisplay from './QuotaDisplay';
import { BuyCreditsButton } from "./PricingModal";
import logo from '../assets/logo4.png';

const Sidebar = ({
  activeTab,
  onTabChange,
  user,
  quotaStatus,
  totalCharsLimit
}) => {
  const menuItems = [
    { id: 'generate', label: 'Generate Speech', icon: <Wand2 size={20} /> },
    { id: 'voiceChanger', label: 'Voice Changer', icon: <AudioWaveform size={20} /> },
    { id: 'voiceCloning', label: 'Voice Cloning', icon: <Mic2 size={20} /> },
    { id: 'speechDenoiser', label: 'Speech Denoiser', icon: <Ear size={20} /> },
  ];
  
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  return (
    <aside className="hidden lg:flex w-[240px] bg-[#181A1B] border-r border-[#3A3D3E] min-h-full flex-col absolute left-0 top-0 h-screen">
      {/* Logo area */}
      <div className="px-4 py-[0.25rem] h-[4.25rem] flex items-center border-b border-transparent ml-[0.25rem] mt-[0.25rem]">
        <img
          src={logo}
          alt="Lucid Logo"
          className="h-[2rem] w-[2.5rem] md:h-[2.75rem] md:w-[3rem]"
        />
        <div className="ml-[0.5rem]">
          <h1 className="text-[clamp(0.9rem,3.75vw,1.31rem)] font-bold text-[#EAECEF]">
            Lucid TTS v1.8
          </h1>
          <p className="text-[0.75rem] text-[#B0B4BB]">
            Authentic Speech.
          </p>
        </div>
      </div>

      {/* Navigation menu */}
      <nav className="flex-grow px-4 pt-3 ml-[0.25rem]">
        <ul className="space-y-1">
          {menuItems.map((item) => (
            <li key={item.id}>
              <button
                onClick={() => onTabChange(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-colors ${
                  activeTab === item.id
                    ? 'bg-[#178A94] text-[#EAECEF]'
                    : 'text-[#B0B4BB] hover:bg-[#2C2F30] hover:text-[#EAECEF]'
                }`}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Bottom section with quota, buy credits, profile, help (in that order) */}
      <div className="px-4 pb-6 ml-[0.25rem] space-y-4 mt-auto">
        {/* Quota display - with adjusted width for desktop */}
        {quotaStatus && (
          <div className="bg-[#2C2F30] p-3 rounded-lg">
            <div className="w-full max-w-[200px] mx-auto">
              <QuotaDisplay 
                remainingChars={quotaStatus.remainingChars || 0}
                totalChars={quotaStatus.totalCharsLimit || totalCharsLimit}
              />
            </div>
          </div>
        )}

        {/* Buy Credits with consistent width */}
        <div className="w-full">
          <BuyCreditsButton />
        </div>

        {/* Profile and logout */}
        <div className="relative w-full">
          <button 
            className="flex items-center gap-2 w-full p-2 rounded-lg bg-[#2C2F30] hover:bg-[#3A3D3E] transition-colors"
            onClick={() => setShowDropdown(!showDropdown)}
            data-dropdown-trigger="true"
          >
            <img 
              src={user?.metadata?.photoURL || `https://ui-avatars.com/api/?name=${user?.email?.charAt(0)}`}
              alt="Profile" 
              className="w-[2rem] h-[2rem] rounded-full border border-[#3A3D3E]"
            />
            <span className="text-[#B0B4BB] text-[0.875rem] flex-grow text-left truncate">
              {user?.displayName || user?.email?.split('@')[0]}
            </span>
          </button>
          
          {showDropdown && (
            <div 
              ref={dropdownRef}
              className="absolute bottom-[110%] left-0 right-0 bg-[#2C2F30] rounded-lg shadow-lg border border-[#3A3D3E] p-2 z-[1500]"
            >
              <button 
                onClick={() => {
                  signOutUser();
                  setShowDropdown(false);
                }}
                className="flex items-center gap-2 w-full px-3 py-2 text-left text-[#B0B4BB] hover:bg-[#181A1B] rounded-lg"
              >
                <LogOut size={16} />
                <span>Sign Out</span>
              </button>
            </div>
          )}
        </div>
        
        {/* Help button - moved to end and with border styling */}
        <a
          href="https://dreamsync.gitbook.io/lucidtts/getting-started/quickstart"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full px-[0.75rem] py-[0.5rem]
                  bg-transparent text-[#B0B4BB] text-[0.875rem] font-medium rounded-lg
                  border border-[#178A94] hover:text-[#EAECEF] transition-colors"
        >
          <HelpCircle size={16} className="text-[#178A94]" />
          Help
        </a>
      </div>
    </aside>
  );
};

export default Sidebar;